import { useMutation } from "@pinia/colada";
import {
	type CreateVibrationCriteria,
	useApi,
	VibrationCriteria,
} from "@soft-db/monitoring-api-client";
import {
	composeCacheEffects,
	removeUndefinedFromListQuery,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";

// --- Keys ---

export const vibrationCriteriasKeys = {
	root: ["vibration-criterias"] as const,

	list: () => [...vibrationCriteriasKeys.root, "list"] as const,

	bySite: (id: string) => [...vibrationCriteriasKeys.list(), "site", id] as const,

	byId: (id: string) => [...vibrationCriteriasKeys.root, "id", id] as const,

	global: () => [...vibrationCriteriasKeys.list(), "global"] as const,
};

// --- Cache Effects ---

function useInvalidateSiteVC() {
	const invalidate = useInvalidator();

	return (vc: VibrationCriteria) => {
		if (vc.site) {
			invalidate({
				keys: [vibrationCriteriasKeys.bySite(vc.site)],
			});
		}
	};
}

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (vc: VibrationCriteria) => {
		update(vibrationCriteriasKeys.byId(vc.id), vc);
	};
}

function useVibrationCriteriaCacheEffects() {
	return composeCacheEffects(useInvalidateSiteVC(), useUpdateCacheById());
}

// --- Queries ---

export function useGlobalCriterias() {
	const api = useApi().vibrationCriterias();

	const { data: globalVibrationCriterias, ...rest } = useScopedQuery({
		key: () => vibrationCriteriasKeys.global(),
		query: () => api.fetchGlobalCriterias(),
		enabled: useQueryEnabled(),
	});

	return {
		globalVibrationCriterias: removeUndefinedFromListQuery(globalVibrationCriterias),
		data: globalVibrationCriterias,
		...rest,
	};
}

export function useSiteCriterias(site: MaybeRefOrGetter<string>) {
	const api = useApi().vibrationCriterias();

	const { data: vibrationCriterias, ...rest } = useScopedQuery({
		key: () => vibrationCriteriasKeys.bySite(toValue(site)),
		query: () => api.fetchSiteCriterias(toValue(site)),
		enabled: useQueryEnabled(site),
	});

	return {
		vibrationCriterias: removeUndefinedFromListQuery(vibrationCriterias),
		data: vibrationCriterias,
		...rest,
	};
}

export function useVibrationCriteria(id: MaybeRefOrGetter<string>) {
	const api = useApi().vibrationCriterias();

	const { data: vibrationCriteria, ...rest } = useScopedQuery({
		key: () => vibrationCriteriasKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		vibrationCriteria,
		data: vibrationCriteria,
		...rest,
	};
}

// --- Mutations ---

export function useCreateVibrationCriteria() {
	const api = useApi().vibrationCriterias();

	return useMutation({
		mutation: ({ site, data }: { site: string; data: CreateVibrationCriteria }) =>
			api.create(site, data),
		onSuccess: useVibrationCriteriaCacheEffects(),
	});
}

export function useUpdateVibrationCriteria() {
	const api = useApi().vibrationCriterias();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: CreateVibrationCriteria }) => api.update(id, data),
		onSuccess: useVibrationCriteriaCacheEffects(),
	});
}

export function useDeleteVibrationCriteria() {
	const api = useApi().vibrationCriterias();

	return useMutation({
		mutation: async (id: string) => {
			const vc = await api.fetch(id);
			await api.delete(id);
			return vc;
		},
		onSuccess: useInvalidateSiteVC(),
	});
}
